import { useMemo } from 'react';
import { MetadataFilters } from '../types/stats';
import { Button } from '../../../components/ui/button';
import { MultiSelect, Option } from '../../../components/ui/multi-select';

type AppliedFilters = {
  project?: string | null;
  subProject?: string[] | string | null;
  pipeId?: string[] | string | null;
  pipeDiameter?: string | null;
  wallThickness?: string[] | string | null;
  shift?: string | null;
  location?: string[] | string | null;
  criticalWeld?: string | null;
  wpsNumber?: string[] | string | null;
};

export type PipeOption = { pipeId: number; displayName: string };

/** Category API response: one key per column, value = array of { id, [columnName]: value } */
export type NdtCategoryData = Record<string, Array<{ id: number; [k: string]: unknown }>>;

export type CategoryFieldConfig = {
  key: keyof AppliedFilters;
  label: string;
  categoryKey: string;
};

export const FilterPanel = ({
  filters,
  selection,
  onChange,
  hideProjectFilter = false,
  preserveProjectOnClear = false,
  categoryData = null,
  categoryFields = [],
  pipeOptions = [],
}: {
  filters: MetadataFilters | null;
  selection: AppliedFilters | null;
  onChange: (next: AppliedFilters) => void;
  hideProjectFilter?: boolean;
  preserveProjectOnClear?: boolean;
  categoryData?: NdtCategoryData | null;
  categoryFields?: CategoryFieldConfig[];
  pipeOptions?: PipeOption[];
}) => {
  const safeSelection = selection || {};
  const useCategoryMode = Boolean(categoryData && categoryFields.length > 0);

  const dropdownOptionsByKey = useMemo(() => {
    if (!useCategoryMode || !categoryData) {
      return {} as Record<string, string[]>;
    }
    const optionsByKey: Record<string, string[]> = {};
    for (const currentField of categoryFields) {
      const arr = categoryData[currentField.categoryKey];
      if (!Array.isArray(arr)) continue;
      // Build allowedIds from OTHER fields only (exclude current field so its dropdown keeps full list for multi-select)
      let allowedIds: Set<number> | null = null;
      for (const field of categoryFields) {
        if (field.key === currentField.key) continue;
        const selectedVal = safeSelection[field.key];
        const fieldArr = categoryData[field.categoryKey];
        if (!Array.isArray(fieldArr)) continue;
        const selectedArr = Array.isArray(selectedVal)
          ? selectedVal
          : selectedVal != null && selectedVal !== ''
            ? [String(selectedVal)]
            : null;
        if (selectedArr && selectedArr.length > 0) {
          const ids = new Set(
            fieldArr
              .filter((item) => selectedArr.includes(String(item[field.categoryKey] ?? '')))
              .map((item) => item.id)
          );
          allowedIds = allowedIds == null ? ids : new Set((Array.from(allowedIds) as number[]).filter((id) => ids.has(id)));
        }
      }
      if (allowedIds == null) {
        const allIds = new Set<number>();
        for (const field of categoryFields) {
          const a = categoryData[field.categoryKey];
          if (Array.isArray(a)) a.forEach((item) => allIds.add(item.id));
        }
        allowedIds = allIds.size > 0 ? allIds : null;
      }
      const filtered = allowedIds ? arr.filter((item) => allowedIds!.has(item.id)) : arr;
      const values = Array.from(
        new Set(
          filtered
            .map((item) => String(item[currentField.categoryKey] ?? '').trim())
            .filter((v) => v !== '')
        )
      ).sort();
      optionsByKey[currentField.key] = values;
    }
    return optionsByKey;
  }, [useCategoryMode, categoryData, categoryFields, safeSelection]);

  const handleSelectMulti = (key: keyof AppliedFilters, value: string[]) => {
    onChange({ ...safeSelection, [key]: value.length > 0 ? value : null });
  };

  const handleClear = () => {
    if (preserveProjectOnClear && safeSelection.project) {
      onChange({ project: safeSelection.project });
    } else {
      onChange({});
    }
  };

  const hasActiveFilters = Object.entries(safeSelection).some(([k, v]) => {
    if (k === 'project') return false;
    if (v == null || v === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });

  const pipeSelect = pipeOptions.length > 0 && (() => {
    const rawPipeSelected = safeSelection.pipeId;
    const selectedPipes: string[] = Array.isArray(rawPipeSelected)
      ? rawPipeSelected
      : rawPipeSelected != null && rawPipeSelected !== ''
        ? [String(rawPipeSelected)]
        : [];
    const pipeMultiOptions: Option[] = pipeOptions.map((pipe) => ({
      value: String(pipe.pipeId),
      label: pipe.displayName,
    }));
    return (
      <div className="flex flex-1 items-center gap-1.5 min-w-[240px]">
        <span className="text-xs text-slate-500 whitespace-nowrap">Pipe Size:</span>
        <MultiSelect
          options={pipeMultiOptions}
          selected={selectedPipes}
          onChange={(value) => handleSelectMulti('pipeId', value)}
          placeholder="Any"
          className="w-full min-w-0"
        />
      </div>
    );
  })();

  if (!useCategoryMode && !filters) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500 shadow-sm">
        Loading filters...
      </div>
    );
  }

  const renderToolbar = (inner: React.ReactNode) => (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <span className="mr-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Filters</span>
      {inner}
      <span className="flex-1" />
      {hasActiveFilters && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto px-2 py-1 text-xs text-brand"
          onClick={handleClear}
        >
          Clear
        </Button>
      )}
    </div>
  );

  if (useCategoryMode) {
    return renderToolbar(
      <>
        {pipeSelect}
        {categoryFields.map(({ key, label }) => {
          const options = dropdownOptionsByKey[key];
          if (!options || options.length === 0) return null;
          const rawSelected = safeSelection[key];
          const selected: string[] = Array.isArray(rawSelected)
            ? rawSelected
            : rawSelected != null && rawSelected !== ''
              ? [String(rawSelected)]
              : [];
          const multiOptions: Option[] = options.map((value) => ({ value, label: value }));
          return (
            <div key={key} className="flex flex-1 items-center gap-1.5 min-w-[200px]">
              <span className="text-xs text-slate-500 whitespace-nowrap">{label}:</span>
              <MultiSelect
                options={multiOptions}
                selected={selected}
                onChange={(value) => handleSelectMulti(key, value)}
                placeholder="Any"
                className="w-full min-w-0"
              />
            </div>
          );
        })}
      </>,
    );
  }

  return renderToolbar(pipeSelect);
};

